import * as Dialog from '@radix-ui/react-dialog';
import { TextSize, Typography } from '@shared/index';
import { X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { QuarterlyBonusEditPopup } from './edit-popup';
import { useQueryClient } from '@tanstack/react-query';
import { QuarterlyHistoryCard } from './history-card';
import BaseButton from '@shared/uikit/base-button';
import {
  useGetTeamQuarterlyHistory,
  useUpdateTeamQuarterlyPlan,
} from '@entities/managers-perfomance';
import { TeamQuarterlyHistory } from '@entities/managers-perfomance/types';
import { QUERY_KEY_MANAGER_PERFORMANCE } from '@entities/managers-perfomance/query-keys';

type Props = {
  open: boolean;
  onClose: VoidFunction;
  year: number;
};

const getCurrentQuarter = () => {
  const month = new Date().getMonth() + 1;

  if (month <= 3) return 1;
  if (month <= 6) return 2;
  if (month <= 9) return 3;

  return 4;
};

const mapQuarters = (data: Array<TeamQuarterlyHistory>) => {
  const quarters: (TeamQuarterlyHistory | null)[] = [null, null, null, null];

  data.forEach(item => {
    if (item.quarter && item.quarter >= 1 && item.quarter <= 4) {
      quarters[item.quarter - 1] = item;
    }
  });

  return quarters;
};

export const QuarterlyHistoryPopup = ({ open, onClose, year }: Props) => {
  const [isEditBonusPopupOpen, setIsEditBonusPopupOpen] = useState(false);

  const updateQuarterlyPlan = useUpdateTeamQuarterlyPlan();
  const { data: teamQuarterlyHistory = [] } = useGetTeamQuarterlyHistory({ year });
  const queryClient = useQueryClient();

  const currentYear = new Date().getFullYear();
  const currentQuarter = getCurrentQuarter();

  const quarters = useMemo(
    () => mapQuarters(teamQuarterlyHistory).slice(0, 4),
    [teamQuarterlyHistory]
  );

  const quarterlyBonusData = useMemo(() => {
    return Array.from({ length: 4 }, (_, quarterIndex) => {
      const quarterNumber = quarterIndex + 1;

      const existingQuarterData = teamQuarterlyHistory.find(
        historyItem => historyItem.quarter === quarterNumber
      );

      return existingQuarterData
        ? {
            id: existingQuarterData.id,
            year: existingQuarterData.year,
            quarter: quarterNumber,
            plan: existingQuarterData.plan,
            hashrate: existingQuarterData.hashrate,
            percentage_change: existingQuarterData.percentage,
          }
        : {
            year: year,
            quarter: quarterNumber,
            plan: 0,
            hashrate: 0,
            percentage_change: 0,
          };
    });
  }, [teamQuarterlyHistory, year]);

  const handleSubmitQuarterlyBonus = async (
    quarters: {
      quarter: number;
      plan: string;
      bonusManagerId?: number;
    }[]
  ) => {
    for (const quarter of quarters) {
      const normalizedPlan = quarter.plan.replace(/\s/g, '').replace(',', '.');
      const existing = teamQuarterlyHistory.find(item => item.quarter === quarter.quarter);
      const bonusManager = existing?.bonus_manager_id ?? quarter.bonusManagerId ?? undefined;
      const isBonusActive = Boolean(existing?.is_bonus_active && bonusManager);

      await updateQuarterlyPlan.fetch({
        year,
        quarter: quarter.quarter,
        plan: normalizedPlan,
        isBonusActive,
        bonusManager,
      });
    }

    queryClient.invalidateQueries({
      queryKey: [QUERY_KEY_MANAGER_PERFORMANCE.TEAM_QUARTERLY_HISTORY, year],
    });

    setIsEditBonusPopupOpen(false);
  };

  return (
    <>
      <Dialog.Root open={open} onOpenChange={isOpen => !isOpen && onClose()}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/50 z-1000" />
          <Dialog.Content
            className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-dark-300 rounded-[10px] z-[9999] shadow-lg"
            style={{ width: '744px', padding: '24px', maxHeight: '90vh', overflowY: 'auto' }}
            onEscapeKeyDown={onClose}
            onPointerDownOutside={onClose}
          >
            <Dialog.Close className="absolute top-4 right-4 cursor-pointer" onClick={onClose}>
              <X size={20} className="text-white" />
            </Dialog.Close>
            <Typography
              title="История по кварталам"
              size={TextSize.size22}
              className="font-semibold text-white mb-5"
            />
            <div className="grid grid-cols-2 gap-4 mb-6">
              {quarters.map((quarter, index) => (
                <QuarterlyHistoryCard
                  key={quarter?.id ?? `quarter-${index + 1}`}
                  quarter={index + 1}
                  item={quarter}
                  currentYear={currentYear}
                  currentQuarter={currentQuarter}
                  year={year}
                />
              ))}
            </div>
            <div className="flex justify-center">
              <BaseButton
                onClick={() => {
                  onClose();
                  setIsEditBonusPopupOpen(true);
                }}
                className="bg-gray-800 w-auto h-auto min-w-[136px] p-[10px] border-none cursor-pointer"
              >
                <Typography
                  text="Установить план"
                  size={TextSize.size14}
                  className="text-white font-medium whitespace-nowrap"
                />
              </BaseButton>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
      <QuarterlyBonusEditPopup
        quarterlyBonus={quarterlyBonusData}
        open={isEditBonusPopupOpen}
        onClose={() => setIsEditBonusPopupOpen(false)}
        onSubmit={handleSubmitQuarterlyBonus}
        year={year}
      />
    </>
  );
};
